import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  ArrowLeft,
  Search,
  MapPin,
  Clock,
  Shield,
  User
} from "lucide-react";
import PeerSupportDisclaimer from "../components/PeerSupportDisclaimer";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ResponderDirectory = ({ user }) => {
  const navigate = useNavigate();
  const [responders, setResponders] = useState([]);
  const [filteredResponders, setFilteredResponders] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedResponder, setSelectedResponder] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadResponders();
  }, []);

  useEffect(() => {
    if (searchTerm) {
      const filtered = responders.filter(r => 
        r.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        r.military_branch?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        r.professional_background?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        r.zip_code?.includes(searchTerm) ||
        r.bio?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredResponders(filtered);
    } else {
      setFilteredResponders(responders);
    }
  }, [searchTerm, responders]);

  const loadResponders = async () => {
    try {
      const response = await axios.get(`${API}/responders/directory`);
      setResponders(response.data);
      setFilteredResponders(response.data);
    } catch (error) {
      console.error("Error loading responders:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const getInitials = (name) => {
    return name?.split(" ").map(n => n[0]).join("").toUpperCase() || "U";
  };

  const availableCount = responders.filter(r => r.is_available).length;

  return (
    <div className="min-h-screen bg-background">
      <div className="va-blue-bar h-2"></div>
      
      {/* Header */}
      <header className="bg-white border-b border-border sticky top-0 z-40">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center gap-4 mb-4">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate("/dashboard")}
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-2">
              <img src="/valor-logo2.png" alt="V.A.L.O.R." className="h-10 w-auto" />
              <span className="font-bold text-foreground" style={{ fontFamily: 'Manrope' }}>
                Responder Directory
              </span>
              <span className="text-xs text-primary font-bold">™</span>
            </div>
          </div>
          
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input
              placeholder="Search by name, branch, or location..."
              className="pl-10"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              data-testid="search-input"
            />
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-6 max-w-4xl">
        {/* Stats */}
        <div className="flex items-center justify-between mb-6">
          <p className="text-muted-foreground">
            {filteredResponders.length} responder{filteredResponders.length !== 1 ? "s" : ""} found
          </p>
          <Badge variant="outline" className="text-green-600 border-green-600">
            {availableCount} currently available
          </Badge>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : filteredResponders.length === 0 ? (
          <Card className="card-valor">
            <CardContent className="py-12 text-center">
              <User className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">
                {searchTerm ? "No responders match your search" : "No approved responders yet"}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {filteredResponders.map(responder => (
              <Card 
                key={responder.user_id} 
                className="card-valor hover:shadow-lg transition-shadow cursor-pointer"
                onClick={() => navigate(`/responder/${responder.user_id}`)}
                data-testid={`responder-${responder.user_id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start gap-4">
                    <Avatar className="w-14 h-14">
                      <AvatarImage src={responder.picture} />
                      <AvatarFallback className="bg-primary text-white text-lg">
                        {getInitials(responder.name)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-foreground" style={{ fontFamily: 'Manrope' }}>
                          {responder.name}
                        </h3>
                        {responder.is_available && (
                          <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                        )}
                        {responder.applicant_type === "civilian" && (
                          <Badge variant="outline" className="text-xs">Civilian</Badge>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground mb-2">
                        {responder.applicant_type === "civilian" ? (
                          <>
                            <Badge variant="secondary">{responder.professional_background}</Badge>
                            {responder.years_of_experience > 0 && (
                              <>
                                <span>•</span>
                                <span>{responder.years_of_experience} years experience</span>
                              </>
                            )}
                          </>
                        ) : (
                          <>
                            <Badge variant="secondary">{responder.military_branch}</Badge>
                            {responder.years_of_service > 0 && (
                              <>
                                <span>•</span>
                                <span>{responder.years_of_service} years service</span>
                              </>
                            )}
                          </>
                        )}
                        {responder.zip_code && (
                          <>
                            <span>•</span>
                            <span className="flex items-center gap-1">
                              <MapPin className="w-3 h-3" />
                              {responder.zip_code}
                            </span>
                          </>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground line-clamp-2">
                        {responder.bio}
                      </p>
                    </div>
                    <div className="hidden sm:block">
                      <Button variant="outline" size="sm">
                        View Profile
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>

      {/* Responder Detail Dialog */}
      <Dialog open={!!selectedResponder} onOpenChange={() => setSelectedResponder(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          {selectedResponder && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-4">
                  <Avatar className="w-16 h-16">
                    <AvatarImage src={selectedResponder.picture} />
                    <AvatarFallback className="bg-primary text-white text-xl">
                      {getInitials(selectedResponder.name)}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                      {selectedResponder.name}
                      {selectedResponder.is_available && (
                        <Badge className="bg-green-600">Available</Badge>
                      )}
                    </DialogTitle>
                    <DialogDescription>
                      {selectedResponder.military_branch} • {selectedResponder.years_of_service} years of service
                    </DialogDescription>
                  </div>
                </div>
              </DialogHeader>

              <div className="space-y-6 py-4">
                {/* Certifications */}
                {selectedResponder.certifications?.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-muted-foreground mb-2 flex items-center gap-2">
                      <Shield className="w-4 h-4" />
                      Certifications
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {selectedResponder.certifications.map(cert => (
                        <Badge key={cert} variant="secondary">{cert}</Badge>
                      ))}
                    </div>
                  </div>
                )}

                {/* Availability */}
                <div>
                  <h4 className="text-sm font-medium text-muted-foreground mb-2 flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    Availability
                  </h4>
                  <p className="text-foreground">
                    {selectedResponder.availability_days?.join(", ") || "Not specified"}
                  </p>
                  {selectedResponder.availability_hours && (
                    <p className="text-sm text-muted-foreground">{selectedResponder.availability_hours}</p>
                  )}
                </div>

                {/* Location */}
                {selectedResponder.zip_code && (
                  <div>
                    <h4 className="text-sm font-medium text-muted-foreground mb-2 flex items-center gap-2">
                      <MapPin className="w-4 h-4" />
                      Location
                    </h4>
                    <p className="text-foreground">Zip Code: {selectedResponder.zip_code}</p>
                  </div>
                )}

                {/* Bio */}
                <div>
                  <h4 className="text-sm font-medium text-muted-foreground mb-2">About</h4>
                  <p className="text-foreground whitespace-pre-wrap">{selectedResponder.bio}</p>
                </div>
              </div>

              <div className="flex justify-end">
                <Button onClick={() => setSelectedResponder(null)}>
                  Close
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Global Footer Disclaimer */}
      <PeerSupportDisclaimer />
    </div>
  );
};

export default ResponderDirectory;
